import type { DashboardWidgetTarget } from '../../models/builder.types'
import type { DashboardRow, WireSection } from '../../models/layout.types'
import type { DashboardWidget } from '../../models/widget.types'
import { BUILDER_OWNED_WIDGET_KEYS } from '../widgetSpec'

import { parseLayout, serializeLayout } from './serialize'

function clone(rows: readonly DashboardRow[]): DashboardRow[] {
  return parseLayout(serializeLayout(rows))
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The three section shapes v3's `addSection` could produce, with its exact widths
 * (`CustomDashboardSection.vue:499-511`). `classic` is deliberately asymmetric — a wide primary
 * block beside a narrow companion — and is the one shape the v4 grid could not build.
 */
export type RowLayout = 'col' | 'classic' | 'grid'

const LAYOUT_WIDTHS: Readonly<Record<RowLayout, readonly number[]>> = {
  col: [100],
  classic: [65, 35],
  grid: [50, 50],
}

/** Structural chrome only: a new cell carries no widget keys, so it renders as an empty block. */
function emptyCell(id: string, width: number): Record<string, unknown> {
  return { id, name: '', width }
}

/**
 * A root section is a tab group in v3 (`addSection:499-511` gives every col a `makeTab()`), so
 * `tabName` seeds each new cell with one empty tab; a nested section is a plain block and gets none.
 */
export function appendRow(
  rows: readonly DashboardRow[],
  layout: RowLayout = 'col',
  id = `section-${Date.now()}`,
  options: { tabName?: string } = {},
): DashboardRow[] {
  const source: WireSection = {
    id,
    name: '',
    cols: LAYOUT_WIDTHS[layout].map((width, index) => {
      const cell = emptyCell(`${id}-col-${index}`, width)
      if (!options.tabName) return cell
      return {
        ...cell,
        tabs: [{ id: `${id}-col-${index}-tab`, name: options.tabName, sections: [] }],
      }
    }),
  }
  return parseLayout([...serializeLayout(rows), source])
}

/**
 * v3 `deleteCol:432-438`: drop one cell, and the row with it once it is empty. The survivor of a
 * two-cell row takes the full width, which v3 forgot — it kept rendering at its old 50/65%.
 */
export function removeCell(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
): DashboardRow[] {
  const sections = serializeLayout(rows).flatMap((section) => {
    if (section.id !== rowId || !Array.isArray(section.cols)) return [section]
    const cols = section.cols.filter((col) => !isRecord(col) || col.id !== cellId)
    if (cols.length === 0) return []
    const [only] = cols
    return [
      { ...section, cols: cols.length === 1 && isRecord(only) ? [{ ...only, width: 100 }] : cols },
    ]
  })
  return parseLayout(sections)
}

export function removeRow(rows: readonly DashboardRow[], rowId: string): DashboardRow[] {
  return clone(rows.filter((row) => row.id !== rowId))
}

export function renameRow(
  rows: readonly DashboardRow[],
  rowId: string,
  name: string,
): DashboardRow[] {
  const sections = serializeLayout(rows).map((section) =>
    section.id === rowId ? { ...section, name } : section,
  )
  return parseLayout(sections)
}

/**
 * Re-key a persisted subtree so a duplicate cannot collide with its original. Only `id` is
 * rewritten; every other key — including the widget bag and anything uninventoried — rides along,
 * which is the same guarantee `serializeLayout` gives.
 */
function withFreshIds(node: Record<string, unknown>, newId: () => string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...node, id: newId() }
  for (const key of ['cols', 'tabs', 'sections']) {
    const children = node[key]
    if (Array.isArray(children)) {
      next[key] = children.map((child) =>
        isRecord(child) ? withFreshIds(child, newId) : (child as unknown),
      )
    }
  }
  return next
}

/**
 * v3 duplicated a *column* into a brand-new full-width section below (`duplicateCol:399-408`),
 * which silently changed the block's width and orphaned its row siblings. v4 duplicates the whole
 * row in place instead: same widths, same cells, fresh ids, inserted directly after the original.
 */
export function duplicateRow(
  rows: readonly DashboardRow[],
  rowId: string,
  newId: () => string = () => `dup-${Math.random().toString(36).slice(2)}`,
): DashboardRow[] {
  const sections = serializeLayout(rows)
  const index = sections.findIndex((section) => section.id === rowId)
  if (index === -1) return clone(rows)
  const original = sections[index]
  if (!original) return clone(rows)
  const copy = withFreshIds(original, newId) as WireSection
  return parseLayout([...sections.slice(0, index + 1), copy, ...sections.slice(index + 1)])
}

export function splitRow(
  rows: readonly DashboardRow[],
  rowId: string,
  layout: Exclude<RowLayout, 'col'> = 'grid',
): DashboardRow[] {
  const [leftWidth = 50, rightWidth = 50] = LAYOUT_WIDTHS[layout]
  const sections = serializeLayout(rows).map((section) => {
    if (section.id !== rowId || !Array.isArray(section.cols) || section.cols.length !== 1) {
      return section
    }
    const source = section.cols[0]
    if (!isRecord(source)) return section
    return {
      ...section,
      cols: [{ ...source, width: leftWidth }, emptyCell(`${rowId}-col-${Date.now()}`, rightWidth)],
    }
  })
  return parseLayout(sections)
}

export function resizeRowCells(
  rows: readonly DashboardRow[],
  rowId: string,
  leftCellId: string,
  rightCellId: string,
  leftWidth: number,
  rightWidth: number,
): DashboardRow[] {
  const sections = serializeLayout(rows).map((section) => {
    if (section.id !== rowId || !Array.isArray(section.cols)) return section
    return {
      ...section,
      cols: section.cols.map((candidate) => {
        if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
          return candidate
        }
        const cell = candidate as Record<string, unknown>
        if (cell.id === leftCellId) return { ...cell, width: leftWidth }
        if (cell.id === rightCellId) return { ...cell, width: rightWidth }
        return cell
      }),
    }
  })
  return parseLayout(sections)
}

export function reorderRows(
  rows: readonly DashboardRow[],
  from: number,
  to: number,
): DashboardRow[] {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
    return clone(rows)
  }
  const next = [...rows]
  const [moved] = next.splice(from, 1)
  if (!moved) return clone(rows)
  next.splice(to, 0, moved)
  return clone(next)
}

/** Replace one cell's inline widget bag while preserving every unrelated row/cell/tab key. */
export function setCellWidget(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  widget: DashboardWidget,
): DashboardRow[] {
  const sections = serializeLayout(rows).map((section) => {
    if (section.id !== rowId || !Array.isArray(section.cols)) return section
    return {
      ...section,
      cols: section.cols.map((candidate) => {
        if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
          return candidate
        }
        const cell = candidate as Record<string, unknown>
        if (cell.id !== cellId) return cell
        const retained = { ...cell }
        for (const key of BUILDER_OWNED_WIDGET_KEYS) delete retained[key]
        return { ...retained, ...widget.wire }
      }),
    }
  })
  return parseLayout(sections)
}

/** Reconnect one recursively-edited tab grid to its parent without rebuilding sibling payloads. */
export function setTabRows(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  tabId: string,
  nextRows: readonly DashboardRow[],
): DashboardRow[] {
  const sections = serializeLayout(rows).map((section) => {
    if (section.id !== rowId || !Array.isArray(section.cols)) return section
    return {
      ...section,
      cols: section.cols.map((candidate) => {
        if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
          return candidate
        }
        const cell = candidate as Record<string, unknown>
        if (cell.id !== cellId || !Array.isArray(cell.tabs)) return cell
        return {
          ...cell,
          tabs: cell.tabs.map((tab) => {
            if (typeof tab !== 'object' || tab === null || Array.isArray(tab)) return tab
            const record = tab as Record<string, unknown>
            return record.id === tabId ? { ...record, sections: serializeLayout(nextRows) } : record
          }),
        }
      }),
    }
  })
  return parseLayout(sections)
}

/**
 * Rewrite one cell's persisted `tabs` array. Unlike `setTabRows`, this reaches a cell whose `tabs`
 * key is missing entirely — adding the first tab to a plain block has to create the array, which
 * `serializeLayout`'s deliberate "never invent a child collection" rule cannot do.
 */
function mapCellTabs(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  map: (tabs: Record<string, unknown>[]) => Record<string, unknown>[] | null,
): DashboardRow[] {
  const sections = serializeLayout(rows).map((section) => {
    if (section.id !== rowId || !Array.isArray(section.cols)) return section
    return {
      ...section,
      cols: section.cols.map((candidate) => {
        if (!isRecord(candidate) || candidate.id !== cellId) return candidate
        const current = Array.isArray(candidate.tabs) ? candidate.tabs.filter(isRecord) : []
        const next = map(current)
        return next === null ? candidate : { ...candidate, tabs: next }
      }),
    }
  })
  return parseLayout(sections)
}

export function addTab(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  tab: { id: string; name: string },
): DashboardRow[] {
  return mapCellTabs(rows, rowId, cellId, (tabs) => [...tabs, { ...tab, sections: [] }])
}

export function renameTab(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  tabId: string,
  name: string,
): DashboardRow[] {
  return mapCellTabs(rows, rowId, cellId, (tabs) =>
    tabs.map((tab) => (tab.id === tabId ? { ...tab, name } : tab)),
  )
}

/**
 * `copy.id` is supplied rather than generated so the caller can select the duplicate immediately;
 * every *descendant* is re-keyed by `newId` so the nested sub-grid cannot share ids with the
 * original it was cloned from.
 */
export function duplicateTab(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  tabId: string,
  copy: { id: string; name: string },
  newId: () => string = () => `dup-${Math.random().toString(36).slice(2)}`,
): DashboardRow[] {
  return mapCellTabs(rows, rowId, cellId, (tabs) => {
    const index = tabs.findIndex((tab) => tab.id === tabId)
    const original = tabs[index]
    if (!original) return null
    const duplicated = { ...withFreshIds(original, newId), ...copy }
    return [...tabs.slice(0, index + 1), duplicated, ...tabs.slice(index + 1)]
  })
}

/**
 * A cell must keep at least one tab, so removing the last one is refused.
 *
 * v3 wrote the same rule as a bare `if (col.tabs.length < 2) return` at the top of `deleteTab`
 * (`:374`) — the menu item stayed enabled and the click did nothing, ledger §C's faked-UX shape.
 * The recreate-a-default branch the acceptance cites (`:376-379`) sits *below* that guard and is
 * therefore unreachable in v3; porting it would have been porting dead code. `canRemoveTab` is
 * exported so the control can be disabled instead of silently ignoring the user.
 */
export function canRemoveTab(cell: { tabs: readonly unknown[] }): boolean {
  return cell.tabs.length > 1
}

export function removeTab(
  rows: readonly DashboardRow[],
  rowId: string,
  cellId: string,
  tabId: string,
): DashboardRow[] {
  return mapCellTabs(rows, rowId, cellId, (tabs) =>
    tabs.length > 1 ? tabs.filter((tab) => tab.id !== tabId) : null,
  )
}

/** Apply a builder result to a top-level or nested cell using the same wire-preserving transforms. */
export function setWidgetAtTarget(
  rows: readonly DashboardRow[],
  target: DashboardWidgetTarget,
  widget: DashboardWidget,
): DashboardRow[] {
  const [ancestor, ...rest] = target.ancestors
  if (!ancestor) return setCellWidget(rows, target.rowId, target.cellId, widget)

  const ownerRow = rows.find((row) => row.id === ancestor.rowId)
  const ownerCell = ownerRow?.cells.find((cell) => cell.id === ancestor.cellId)
  const tab = ownerCell?.tabs.find((entry) => entry.id === ancestor.tabId)
  if (!tab) return clone(rows)
  const nested = setWidgetAtTarget(tab.rows, { ...target, ancestors: rest }, widget)
  return setTabRows(rows, ancestor.rowId, ancestor.cellId, ancestor.tabId, nested)
}
